import { z } from "zod";
import { BOW_DIRECTIONS, PAYMENT_METHODS, VESSEL_TYPES } from "../constants";
import { connectionsSchema, idSchema, isoDate, isoDateTime, metres, positiveMetres } from "./common";
import { buyerSchema, registrationSchema } from "./entities";

/** Operation types the dock app can queue while offline (spec §6.5). */
export const SYNC_OP_TYPES = [
  "stay.arrive",
  "stay.depart",
  "stay.extend",
  "stay.move",
  "stay.cancel",
  "invoice.issue",
  "absence.create",
] as const;
export type SyncOpType = (typeof SYNC_OP_TYPES)[number];

export const syncVesselSchema = z.object({
  id: idSchema.optional(),
  registration: registrationSchema,
  name: z.string().trim().max(200).nullish(),
  type: z.enum(VESSEL_TYPES).default("motor"),
  lengthM: positiveMetres,
  beamM: positiveMetres.nullish(),
  draftM: positiveMetres.nullish(),
});

export const stayArrivePayload = z.object({
  id: idSchema,
  portId: idSchema,
  vessel: syncVesselSchema,
  quayId: idSchema.nullish(),
  berthId: idSchema.nullish(),
  positionM: metres.nullish(),
  mooringMode: z.enum(["stern_to", "side_to"]).nullish(),
  raftedOnStayId: idSchema.nullish(),
  bowDirection: z.enum(BOW_DIRECTIONS).nullish(),
  arrivedAt: isoDateTime,
  expectedDeparture: isoDateTime.nullish(),
  connections: connectionsSchema.default([]),
  phone: z.string().trim().max(40).nullish(),
  photoKey: z.string().max(300).nullish(),
  notes: z.string().trim().max(2000).nullish(),
});
export type StayArrivePayload = z.infer<typeof stayArrivePayload>;

export const stayDepartPayload = z.object({ id: idSchema, departedAt: isoDateTime });
export const stayExtendPayload = z.object({ id: idSchema, expectedDeparture: isoDateTime });
export const stayMovePayload = z.object({
  id: idSchema,
  quayId: idSchema.nullable(),
  berthId: idSchema.nullable(),
  positionM: metres.nullable(),
  mooringMode: z.enum(["stern_to", "side_to"]).nullish(),
  raftedOnStayId: idSchema.nullish(),
  bowDirection: z.enum(BOW_DIRECTIONS).nullish(),
});
export const stayCancelPayload = z.object({ id: idSchema });

export const invoiceLinePayload = z.object({
  kind: z.enum(["berth", "minimum", "connection", "metered", "manual", "contract"]),
  description: z.string().min(1).max(300),
  quantity: z.number().min(-100000).max(100000),
  unit: z.enum(["day", "kwh", "m3", "pcs"]),
  unitPriceCents: z.number().int(),
  amountCents: z.number().int(),
});
export type InvoiceLinePayload = z.infer<typeof invoiceLinePayload>;

export const invoiceIssuePayload = z.object({
  id: idSchema,
  portId: idSchema,
  stayId: idSchema.nullish(),
  sequence: z.number().int().positive(),
  issuedAt: isoDateTime,
  buyer: buyerSchema.default({ type: "anonymous" }),
  paymentMethod: z.enum(PAYMENT_METHODS),
  lines: z.array(invoiceLinePayload).min(1).max(50),
  netCents: z.number().int(),
  vatCents: z.number().int(),
  totalCents: z.number().int(),
  vatRatePercent: z.number().min(0).max(50),
  billUntil: isoDateTime.nullish(),
  publicToken: z.string().min(16).max(100),
  paid: z.boolean().default(true),
  locale: z.enum(["hr", "en"]).default("hr"),
});
export type InvoiceIssuePayload = z.infer<typeof invoiceIssuePayload>;

export const absenceCreatePayload = z.object({
  id: idSchema,
  berthId: idSchema,
  freeFrom: isoDate,
  freeUntil: isoDate,
});

export const SYNC_PAYLOADS = {
  "stay.arrive": stayArrivePayload,
  "stay.depart": stayDepartPayload,
  "stay.extend": stayExtendPayload,
  "stay.move": stayMovePayload,
  "stay.cancel": stayCancelPayload,
  "invoice.issue": invoiceIssuePayload,
  "absence.create": absenceCreatePayload,
} as const satisfies Record<SyncOpType, z.ZodType>;

export type SyncPayload<T extends SyncOpType> = z.infer<(typeof SYNC_PAYLOADS)[T]>;

/** Envelope of one queued operation — exactly the shape from spec §6.5. */
export const syncOpSchema = z.object({
  op_id: idSchema,
  type: z.enum(SYNC_OP_TYPES),
  /** Entity kind the op targets (`stay`, `invoice`, `absence`). The id is in the payload. */
  entity: z.enum(["stay", "invoice", "absence"]),
  payload: z.unknown(),
  client_time: isoDateTime,
  device_id: idSchema,
});
export type SyncOp = z.infer<typeof syncOpSchema>;

export const syncRequestSchema = z.object({ ops: z.array(syncOpSchema).min(1).max(200) });

export type SyncOpStatus = "applied" | "duplicate" | "superseded" | "rejected";
export interface SyncOpResult {
  op_id: string;
  status: SyncOpStatus;
  error?: { code: string; message: string };
  /** Server-side warnings, e.g. `quay_overbooked`. */
  warnings?: string[];
}
export interface SyncResponse {
  results: SyncOpResult[];
  serverTime: string;
}

export const OP_ENTITY: Record<SyncOpType, SyncOp["entity"]> = {
  "stay.arrive": "stay",
  "stay.depart": "stay",
  "stay.extend": "stay",
  "stay.move": "stay",
  "stay.cancel": "stay",
  "invoice.issue": "invoice",
  "absence.create": "absence",
};
